import Stack from "@mui/material/Stack";
import { skipToken } from "@reduxjs/toolkit/query";
import { showErrorSnackbar } from "_shared/_errors/error-snackbar";
import EntityMember from "_shared/entity-members/entity-member";
import { enqueueSnackbar } from "_shared/_providers/snackbars/snackbars";
import { useMemo, useState, type ReactNode } from "react";
import useSessionUser from "permissions/session/use-session-user";
import { useSession } from "users/__session/session-context";
import { MaskedTeam } from "work/masked-fields";
import { useGetProposalByIdQuery } from "work/proposals/_api/proposal-api";
import {
  useAcceptProposalLeaderHandoverMutation,
  useApproveProposalLeaderHandoverMutation,
  useDeclineProposalLeaderHandoverMutation,
  useRequestProposalLeaderHandoverMutation,
  useWithdrawProposalLeaderHandoverMutation,
} from "work/proposals/leader-handover/_api/proposal-leader-handover-api";
import type { TeamLeaderRoleAction } from "work/proposals/leader-handover/_views/team-leader-role-cell";
import Proposal from "work/proposals/proposal";
import TeamLeaderHandover, { WorkTeamSide } from "work/work-agreements/teams/leader-handover/team-leader-handover";
import LeaderHandoverBanner from "work/work-agreements/teams/leader-handover/_views/leader-handover-banner";
import LeaderHandoverDialog, {
  LeaderHandoverChoice,
} from "work/work-agreements/teams/leader-handover/_views/leader-handover-dialog";

type OpenDialog = { side: WorkTeamSide; proposedByTheOtherSide: boolean } | undefined;

export type ProposalLeaderHandover = {
  /**
   * Offering a team, drawn in the leader row of each team's roster (#6260). They were secondary
   * actions in the page header (#6099), where they read as things to do to the proposal rather than
   * to the one person they change.
   */
  clientLeaderActions: TeamLeaderRoleAction[];
  vendorLeaderActions: TeamLeaderRoleAction[];
  /**
   * The handovers waiting on an answer. `undefined` when there are none, so the page can leave out
   * the space it would otherwise give them.
   */
  notices: ReactNode;
  /** The dialog an action opens. It draws over the page, so where the page puts it does not matter. */
  dialog: ReactNode;
};

/**
 * Changing who leads a team while the proposal is negotiated (#5836). A leader hands their own team
 * over; the client team leader may also propose a new vendor team leader. The successor accepts, and
 * a vendor team change is approved by the other side's leader, before the negotiation records it as a
 * fact both parties read. Offered only once the proposal has been sent and until it is hired or
 * archived: before it is sent, the side drafting it simply chooses its leader on the draft.
 */
export default function useProposalLeaderHandover(proposal: Proposal | undefined): ProposalLeaderHandover {
  const session = useSession();
  const sessionUser = useSessionUser();
  const proposalIdValue = proposal?.id?.value;
  // The negotiation envelope does not carry pending handovers -- they are not proposal content -- so
  // they are read from the proposal itself, which the server re-sends whenever one changes.
  const proposalQuery = useGetProposalByIdQuery(proposalIdValue ? { proposalId: proposalIdValue, session } : skipToken);
  const pendingJSON = proposalQuery.data?.pendingLeaderHandovers;
  const pendingLeaderHandovers = useMemo(() => TeamLeaderHandover.fromJSONArray(pendingJSON), [pendingJSON]);

  const [requestHandover] = useRequestProposalLeaderHandoverMutation();
  const [acceptHandover] = useAcceptProposalLeaderHandoverMutation();
  const [approveHandover] = useApproveProposalLeaderHandoverMutation();
  const [declineHandover] = useDeclineProposalLeaderHandoverMutation();
  const [withdrawHandover] = useWithdrawProposalLeaderHandoverMutation();
  const [openDialog, setOpenDialog] = useState<OpenDialog>(undefined);
  const [busy, setBusy] = useState(false);

  if (!proposal || !proposalIdValue) {
    return { clientLeaderActions: [], vendorLeaderActions: [], notices: undefined, dialog: undefined };
  }

  const viewer =
    session.user?.id && session.user.legalEntityId
      ? new EntityMember({ userId: session.user.id, legalEntityId: session.user.legalEntityId })
      : undefined;
  const clientTeam = MaskedTeam.isPresentNotMasked(proposal.clientTeam) ? proposal.clientTeam : undefined;
  const vendorTeam = MaskedTeam.isPresentNotMasked(proposal.vendorTeam) ? proposal.vendorTeam : undefined;
  const leadsClientTeam = Boolean(clientTeam?.leader?.isEqualTo(viewer));
  const leadsVendorTeam = Boolean(vendorTeam?.leader?.isEqualTo(viewer));
  const underNegotiation = proposal.isSubmitted && !proposal.isArchived && !proposal.isHired;
  const pendingFor = (side: WorkTeamSide) => pendingLeaderHandovers.some((handover) => handover.side === side);
  const offerTeam = (side: WorkTeamSide, proposedByTheOtherSide: boolean) =>
    setOpenDialog({ side, proposedByTheOtherSide });

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true);
    const error = await action().then(
      () => undefined,
      (failure: unknown) => failure ?? new Error("The handover could not be completed."),
    );
    setBusy(false);
    if (error === undefined) {
      enqueueSnackbar(success, { variant: "success" });
      return true;
    }
    showErrorSnackbar(error, { doing: "handing over the team", operation: "proposal.leader-handover" });
    return false;
  }

  async function offer(choice: LeaderHandoverChoice) {
    if (!openDialog || !proposalIdValue) return;
    const done = await run(
      () =>
        requestHandover({
          proposalId: proposalIdValue,
          side: openDialog.side,
          successor: choice.successor,
          outgoingLeaderStays: choice.outgoingLeaderStays,
          grantSigningAuthority: choice.grantSigningAuthority,
        }).unwrap(),
      "The team was offered. Nothing changes until everybody it waits on says yes.",
    );
    if (done) setOpenDialog(undefined);
  }

  // Each action sits in the leader row of the team it changes (#6260): a leader hands over their own
  // team from their own row, and the client team leader proposes a new vendor team leader from the
  // vendor team's. The full action is the link's name and the dialog's title; the link says only as
  // much as the narrow Role column holds.
  const clientLeaderActions: TeamLeaderRoleAction[] =
    underNegotiation && leadsClientTeam && !pendingFor(WorkTeamSide.Client)
      ? [
          {
            key: "hand-over-client-team",
            label: "Hand Over",
            ariaLabel: "Hand over the client team",
            disabled: busy,
            onClick: () => offerTeam(WorkTeamSide.Client, false),
          },
        ]
      : [];
  const vendorLeaderActions: TeamLeaderRoleAction[] =
    underNegotiation && !pendingFor(WorkTeamSide.Vendor)
      ? [
          ...(leadsVendorTeam
            ? [
                {
                  key: "hand-over-vendor-team",
                  label: "Hand Over",
                  ariaLabel: "Hand over the vendor team",
                  disabled: busy,
                  onClick: () => offerTeam(WorkTeamSide.Vendor, false),
                },
              ]
            : []),
          ...(leadsClientTeam && vendorTeam?.leader
            ? [
                {
                  key: "propose-vendor-team-leader",
                  label: "Propose New",
                  ariaLabel: "Propose a new vendor team leader",
                  disabled: busy,
                  onClick: () => offerTeam(WorkTeamSide.Vendor, true),
                },
              ]
            : []),
        ]
      : [];

  const dialogTeam = openDialog?.side === WorkTeamSide.Client ? clientTeam : vendorTeam;
  const otherTeam = openDialog?.side === WorkTeamSide.Client ? vendorTeam : clientTeam;
  const otherSideUserIds = otherTeam
    ? [...(otherTeam.leader ? [otherTeam.leader.userId.value] : []), ...otherTeam.members.map((member) => member.userId.value)]
    : [];
  const dialog = openDialog && dialogTeam?.leader ? (
    <LeaderHandoverDialog
      open
      side={openDialog.side}
      outgoingLeader={dialogTeam.leader}
      members={dialogTeam.members}
      excludedUserIds={otherSideUserIds}
      canGrantSigningAuthority={sessionUser?.canGrantSigningAuthority() ?? false}
      awaitsTheOtherSide={openDialog.side === WorkTeamSide.Vendor}
      proposedByTheOtherSide={openDialog.proposedByTheOtherSide}
      submitting={busy}
      onSubmit={(choice) => void offer(choice)}
      onCancel={() => setOpenDialog(undefined)}
    />
  ) : undefined;

  if (pendingLeaderHandovers.length === 0) {
    return { clientLeaderActions, vendorLeaderActions, notices: undefined, dialog };
  }

  return {
    clientLeaderActions,
    vendorLeaderActions,
    dialog,
    notices: (
      <Stack spacing={1}>
        {pendingLeaderHandovers.map((handover) => (
          <LeaderHandoverBanner
            key={handover.id.value}
            handover={handover}
            viewer={viewer}
            busy={busy}
            canGrantSigningAuthority={sessionUser?.canGrantSigningAuthority() ?? false}
            onAccept={() => void run(() => acceptHandover({ proposalId: proposalIdValue }).unwrap(), "You accepted the team.")}
            onDecline={() =>
              void run(() => declineHandover({ proposalId: proposalIdValue }).unwrap(), "You declined the handover.")
            }
            onWithdraw={() =>
              void run(
                () => withdrawHandover({ proposalId: proposalIdValue, side: handover.side }).unwrap(),
                "You withdrew the handover.",
              )
            }
            onApprove={(choice) =>
              void run(
                () => approveHandover({ proposalId: proposalIdValue, ...choice }).unwrap(),
                "You approved the new leader.",
              )
            }
          />
        ))}
      </Stack>
    ),
  };
}
